import { Modal } from '@navozyme/uikit/dist/molecules/Modal';

import { NotificationAlert } from 'features/global/alertModal/alertModal';
import PageHeader from 'features/layout/components/PageHeader';

import DocumentPreview from '../../../../forms/components/DocumentPreview';
import Page from '../../../../layout/components/Page';
import CrewListStep from '../../crew/components/CrewListStep';
import SkipAndNextButtons from '../../port-call-data/components/SkipAndNextButtons';

import documentIcon from '../../../styles/images/document.svg';
import downloadIcon from '../../../styles/images/download-icon.svg';
import PortInfo from '../../../styles/images/PortInfo.svg';

type PersonListFormProps = {
  type: 'crew' | 'passengers';
  title: string;
  nextPath: string;
  backPath: string;
  stepsDisabled?: boolean;
  isPersonListSubmmitedToNMap?: boolean;
  submitDisabled?: boolean;
  onSubmit: () => void;

  isPreviousOptionDisplayed?: boolean;
  lastPortCallUid?: string | null;
  lastPortCallPort?: string | null;
  lastPortCallEta?: string | null;
  getPreviousCrewList?: () => void;

  currentPersonListPreview?: {
    filename: string;
    lastUpdated: string | null | undefined;
  };
  currentPersonPDFPreview?: {
    filename: string;
    lastUpdated: string | null | undefined;
  };
  handleDownloadTemplate: () => void;
  handleDownloadPersonList: () => void;
  handleDownloadPersonPDF: () => void;
  handlePDFFileChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    extension: string
  ) => void;
  handleFileChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    extension: string
  ) => void;
  handleUploadPersonList: () => void;
  handleUploadPersonListPDF: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  pdfFileInputRef: React.RefObject<HTMLInputElement | null>;
  isDeleteFileModalOpen: boolean;
  setIsDeleteFileModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleDeletePersonList: () => void;
  setFileToDelete: React.Dispatch<
    React.SetStateAction<'excel/csv' | 'pdf' | undefined>
  >;

  isNotificationDisplaying: { message: string } | boolean;
  setIsNotificationDisplaying: React.Dispatch<
    React.SetStateAction<{ message: string } | boolean>
  >;
  notificationAlertType?: string;
  notificationTitle?: string;
};

export const PDF_EXTENSION = '.pdf';
const ALLOWED_EXTENSIONS = '.xls, .xlsx, .csv';

const PersonListForm = ({
  type,
  title,
  nextPath,
  backPath,
  stepsDisabled,
  isPersonListSubmmitedToNMap,
  submitDisabled,
  onSubmit,

  isPreviousOptionDisplayed = false,
  lastPortCallUid,
  lastPortCallPort,
  lastPortCallEta,
  getPreviousCrewList,

  currentPersonListPreview,
  currentPersonPDFPreview,
  handleDownloadTemplate,
  handleDownloadPersonList,
  handleDownloadPersonPDF,
  handlePDFFileChange,
  handleFileChange,
  handleUploadPersonList,
  handleUploadPersonListPDF,
  fileInputRef,
  pdfFileInputRef,
  isDeleteFileModalOpen,
  setIsDeleteFileModalOpen,
  handleDeletePersonList,
  setFileToDelete,

  isNotificationDisplaying,
  setIsNotificationDisplaying,
  notificationAlertType,
  notificationTitle,
}: PersonListFormProps) => {
  function showDate(lastPortCallEta: string) {
    const date = new Date(lastPortCallEta);
    const formatted = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date);

    return formatted;
  }

  const typeTitle = type.charAt(0).toUpperCase() + type.slice(1);

  return (
    <Page>
      <div className="crew">
        <PageHeader title={title} />
        <NotificationAlert
          isOpened={!!isNotificationDisplaying}
          setIsOpened={setIsNotificationDisplaying}
          id="alert-notification"
          alertType={notificationAlertType}
          title={notificationTitle}
          message={(isNotificationDisplaying as { message: string })?.message}
          timeAutoClose={5000}
          autoClose={true}
        />
        <Modal
          id="delete-person-file-modal"
          width={600}
          isOpen={isDeleteFileModalOpen}
          onModalClose={() => setIsDeleteFileModalOpen(false)}
          iconColor="var(--nmap-500)"
          title={'Are you sure you want to remove this file?'}
          subTitle={'You will be able to attach it later again.'}>
          {
            <Modal.Actions
              cancelLabel="Cancel"
              cancelColor="grey700"
              actionCancel={() => {
                setIsDeleteFileModalOpen(false);
              }}
              actionAction={handleDeletePersonList}
              actionLabel="Remove"
              actionColor="nmap500"
            />
          }
        </Modal>
        <div className="crew-steps">
          <CrewListStep
            number={1}
            title={'Download Template'}
            subtitle={`Download the ${type} list template to fill in your data.`}
            actionComponent={
              <button
                id="download_template"
                className="crew-steps__button"
                onClick={handleDownloadTemplate}>
                <img src={downloadIcon} alt="download-icon" />
                Download template
              </button>
            }
          />
          <CrewListStep
            number={2}
            title={`Attach ${typeTitle} List`}
            subtitle={`Attach your completed ${type} list file. Supported formats: ${ALLOWED_EXTENSIONS}`}
            isStepDisabled={isPersonListSubmmitedToNMap}
            actionComponent={
              <>
                {currentPersonListPreview ? (
                  <DocumentPreview
                    file={currentPersonListPreview}
                    handleDownload={handleDownloadPersonList}
                    hideOpen
                    hideDownload={false}
                    handleDelete={() => {
                      setFileToDelete('excel/csv');
                      setIsDeleteFileModalOpen(true);
                    }}
                    isDisabled={isPersonListSubmmitedToNMap}
                  />
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <button
                        id="attach_passengers_list"
                        className="crew-steps__button"
                        onClick={handleUploadPersonList}>
                        <img
                          src={downloadIcon}
                          alt="attach-icon"
                          className="crew-steps__button_upload"
                        />
                        {`Attach ${type} list`}
                      </button>
                      {isPreviousOptionDisplayed ? (
                        <>
                          <p>or</p>
                          <button
                            id="use_previous_crew_list"
                            className="crew-steps__button"
                            onClick={getPreviousCrewList}
                            disabled={!lastPortCallUid}>
                            <img
                              src={documentIcon}
                              alt="attach-icon"
                              className="crew-steps__button_documentIcon"
                            />
                            Use previous crew list
                          </button>
                        </>
                      ) : null}
                    </div>
                    {lastPortCallUid ? (
                      <div
                        style={{
                          display: 'flex',
                          paddingLeft: '280px',
                          marginTop: '-4px',
                        }}>
                        <img src={PortInfo} alt="attach-icon" />
                        <p
                          id="last_portcall_port"
                          style={{
                            paddingLeft: '10px',
                            fontSize: '12px',
                            color: '#666666',
                            fontWeight: 500,
                            fontStyle: 'Medium',
                            lineHeight: '12px',
                          }}>
                          Port of {lastPortCallPort},{' '}
                          {lastPortCallEta ? showDate(lastPortCallEta) : ''}
                        </p>
                      </div>
                    ) : null}
                    <input
                      id="person_list_file_input"
                      type="file"
                      accept={ALLOWED_EXTENSIONS}
                      style={{ display: 'none' }}
                      ref={fileInputRef}
                      onChange={(event) =>
                        handleFileChange(event, ALLOWED_EXTENSIONS)
                      }
                    />
                  </>
                )}
              </>
            }
          />
          <CrewListStep
            number={3}
            title={'Attach Signed & Stamped PDF'}
            subtitle={`Attach the signed and stamped version of your ${type} list (optional).`}
            isStepDisabled={stepsDisabled}
            actionComponent={
              <>
                {currentPersonPDFPreview ? (
                  <DocumentPreview
                    file={currentPersonPDFPreview}
                    handleDownload={handleDownloadPersonPDF}
                    hideOpen
                    hideDownload={false}
                    handleDelete={() => {
                      setFileToDelete('pdf');
                      setIsDeleteFileModalOpen(true);
                    }}
                    isDisabled={isPersonListSubmmitedToNMap}
                  />
                ) : (
                  <>
                    <button
                      id="attach_signed_pdf"
                      className="crew-steps__button"
                      onClick={handleUploadPersonListPDF}
                      disabled={stepsDisabled}>
                      <img
                        src={downloadIcon}
                        alt="attach-icon"
                        className="crew-steps__button_upload"
                      />
                      Attach signed & stamped pdf
                    </button>
                    <input
                      id="passengers_list_pdf_input"
                      type="file"
                      accept={PDF_EXTENSION}
                      style={{ display: 'none' }}
                      ref={pdfFileInputRef}
                      onChange={(event) =>
                        handlePDFFileChange(event, PDF_EXTENSION)
                      }
                    />
                  </>
                )}
              </>
            }
          />
          <CrewListStep
            number={4}
            title={'Submit'}
            subtitle={`Click the Submit button at the bottom of the page when you're ready to proceed.`}
            isStepDisabled={stepsDisabled}
          />
        </div>
        <SkipAndNextButtons
          next={nextPath}
          submitDisabled={submitDisabled}
          onSubmitClick={onSubmit}
          back={backPath}
          submitLabel="Submit"
        />
      </div>
    </Page>
  );
};

export default PersonListForm;
